/*
  Register/index.js - Root Register Component
*/

// NPM
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Modal,
  Link as MuiLink,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  Typography,
  Alert,
  Box,
  Divider,
  Grid,
  Stack,
  InputLabel,
} from '@mui/material';
import { ArrowForward, Visibility, VisibilityOff } from '@mui/icons-material';

// COMPONENTS
import BackButton from '../../components/common/BackButton';
import FullscreenLayout from '../../components/layouts/FullscreenLayout';
import HelpTooltip from '../../components/common/HelpTooltip';
import CustomModal from '../../components/common/CustomModal';
import LoaderButton from '../../components/common/LoaderButton';

// I18N
import RegisterFormattedMessages from '../../messages/RegisterFormattedMessages';

// NETWORK
import useRegisterRequest from '../../requests/RegisterRequest';

// UTILS
import {
  validateName,
  validateNumberForCountry,
  sanitizeNumber,
  validatePassword,
  validateEmail,
} from '../../utils/validate';

const Register = ({ navigate, errorHandler }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validPassword, setValidPassword] = useState(true);
  const [validConfirmPassword, setValidConfirmPassword] = useState(true);
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [productOfInterest, setProductOfInterest] = useState('');
  const [extraInfo, setExtraInfo] = useState('');
  const [hasRegistered, setHasRegistered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [signUpAgreement, setSignUpAgreement] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendingCode, setResendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [canResendCode, setCanResendCode] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const { registerNewCustomer, verifyPhoneNumberCode, generateResendCode } =
    useRegisterRequest();

  useEffect(() => {
    if (!password || password === '') {
      setValidPassword(true);
    } else {
      if (validatePassword(password)) {
        setValidPassword(true);
      } else {
        setValidPassword(false);
      }
    }
    if (confirmPassword && confirmPassword !== password) {
      setValidConfirmPassword(false);
    } else {
      setValidConfirmPassword(true);
    }
  }, [password, confirmPassword]);

  /*
    Validates form
  */
  const isFormValid = () => {
    let isValidForm = false;
    let errorText = '';

    let phoneNumberSanitized;
    try {
      phoneNumberSanitized = sanitizeNumber(phoneNumber, countryCode);
    } catch (e) {
      console.error('error with phone parsing:', e);
      phoneNumberSanitized = null;
    }

    if (!validateName(firstName))
      errorText = RegisterFormattedMessages.errorFirstName;
    else if (!validateName(lastName))
      errorText = RegisterFormattedMessages.errorLastName;
    else if (!validateEmail(email))
      errorText = RegisterFormattedMessages.errorEmailCheck;
    else if (!phoneNumberSanitized)
      errorText = RegisterFormattedMessages.errorMissingPhoneNumber;
    else if (
      !validateNumberForCountry(phoneNumberSanitized, countryCode) ||
      phoneNumberSanitized.trim() === ''
    )
      errorText = RegisterFormattedMessages.errorPhoneNumber;
    else if (!password || !confirmPassword)
      errorText = RegisterFormattedMessages.errorPassword;
    else if (!validatePassword(password))
      errorText = RegisterFormattedMessages.errorCheckPasswordStrong;
    else if (!(password === confirmPassword))
      errorText = RegisterFormattedMessages.errorPasswordMatch;
    else if (!validateName(company))
      errorText = RegisterFormattedMessages.errorCompany;
    else if (!country) errorText = RegisterFormattedMessages.errorCountry;

    if (!errorText) isValidForm = true;
    else setErrorText(errorText);
    return isValidForm;
  };

  /**
   * Goes back to the login page.
   */
  const backToLogin = () => {
    navigate('/');
  };

  const contactSupport = () => {
    window.open('https://redoxygen.com/contact-us/', '_blank');
  };

  /*
    Convenience function to handle the updating of a form element
  */
  const handleInputChange = (e) => {
    const target = e.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (name === 'country') {
      const { country, code } = e.target;
      setCountry(country);
      setCountryCode(code);
      return;
    }

    switch (name) {
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'phoneNumber':
        setPhoneNumber(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
      case 'company':
        setCompany(value);
        break;
      case 'productOfInterest':
        setProductOfInterest(value);
        break;
      case 'extraInfo':
        setExtraInfo(value);
        break;
      case 'signUpAgreement':
        setSignUpAgreement(value);
        break;
      default:
        break;
    }
  };

  /*
    Sets error text if in an error state, else returns nulls
  */
  const setError = () => {
    if (!hasError) return null;

    return <Alert severity="error">{errorText}</Alert>;
  };

  /*
    Handles form submission
  */
  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsLoading(true);
    setHasError(false);
    setErrorText('');
    try {
      if (!isFormValid()) {
        return setHasError(true), setIsLoading(false);
      }

      const body = {
        email,
        firstName,
        lastName,
        phoneNumber: sanitizeNumber(phoneNumber, countryCode),
        password,
        confirmPassword,
        company,
        country,
        countryCode,
      };

      // Make request to start Customer registration
      let error = await registerNewCustomer(body, errorHandler);
      // If no error, go to next state
      if (!error) {
        return (
          setIsLoading(false),
          setHasError(false),
          setShowVerification(true),
          setCanResendCode(true),
          setResendingCode(false),
          setVerifyingCode(false)
        );
      }

      return setIsLoading(false), setHasError(true), setErrorText(error.error);
    } catch (e) {
      console.log('Have regi cust network error:', e);
      return (
        setIsLoading(false), setHasError(true), setErrorText('Network Error')
      );
    }
  };

  const countryDropDown = () => {
    const countries = [
      { name: 'Australia', code: 'AU' },
      { name: 'Belgium', code: 'BE' },
      { name: 'Canada', code: 'CA' },
      { name: 'Denmark', code: 'DK' },
      { name: 'Finland', code: 'FI' },
      { name: 'Ireland', code: 'IE' },
      { name: 'Mexico', code: 'MX' },
      { name: 'Netherlands', code: 'NL' },
      { name: 'New Zealand', code: 'NZ' },
      { name: 'Norway', code: 'NO' },
      { name: 'Singapore', code: 'SG' },
      { name: 'Sweden', code: 'SE' },
      { name: 'Switzerland', code: 'CH' },
      { name: 'United Kingdom', code: 'UK' },
      { name: 'United States of America', code: 'US' },
    ];

    return (
      <Select
        name="country"
        size="small"
        fullWidth
        value={country || 'none'}
        onChange={(_, newValue) => {
          handleInputChange({
            target: {
              name: 'country',
              country: newValue.props.country,
              code: newValue.props.code,
            },
          });
        }}
        displayEmpty
        required
        inputProps={{ 'aria-label': 'Country' }}
      >
        {!country && (
          <MenuItem value="none" disabled>
            Please choose an option
          </MenuItem>
        )}
        {countries.map((c) => (
          <MenuItem key={c.name} value={c.name} country={c.name} code={c.code}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const closeVerification = (event, reason) => {
    if (reason === 'backdropClick') {
      return false;
    }
    setShowVerification(false);
    setHasError(false);
    setErrorText('');
  };

  /*
   handle verifying code and create new trial customer
  */
  const handleVerification = async () => {
    setHasError(false);
    setErrorText('');
    setVerifyingCode(true);

    if (!verificationCode) {
      return (
        setHasError(true),
        setErrorText('Please enter the code.'),
        setVerifyingCode(false)
      );
    }
    let body = {
      code: verificationCode,
      email,
      password,
    };
    // Send the verification code to backend and return the response
    let result = await verifyPhoneNumberCode(body, errorHandler);

    // Success
    if (!result) {
      setShowVerification(false);
      setHasRegistered(true);
    }
    // Failure
    else {
      setHasError(true);
      setErrorText(result.error);
      setVerifyingCode(false);
    }
  };

  /*
    handle resend verification code generation
  */
  const handleResendCode = async () => {
    setResendingCode(true);
    setHasError(false);
    setErrorText('');
    let body = {
      email,
      phoneNumber,
    };
    let result = await generateResendCode(body, errorHandler);
    if (!result) {
      setResendingCode(false);
    } else {
      let stateChange = {
        hasError: true,
        errorText: result.error,
      };
      if (
        result.error ===
        'You have sent too many verification code requests. Please contact support.'
      )
        stateChange.canResendCode = false;
      setHasError(true);
      setErrorText(result.error);
      setCanResendCode(false);
    }
  };

  const showVerificationDialog = () => {
    let resendLink = (
      <MuiLink tabIndex={0} onClick={handleResendCode}>
        {RegisterFormattedMessages.resend}
      </MuiLink>
    );

    if (!canResendCode) resendLink = null;
    else if (resendingCode)
      resendLink = (
        <Typography component="span">
          {RegisterFormattedMessages.sending}
        </Typography>
      );

    return (
      <CustomModal
        open={showVerification}
        handleClose={closeVerification}
        onExited={() => setVerifyingCode(false)}
        disableEscapeKeyDown
        title={RegisterFormattedMessages.verificationModalTitle}
      >
        <Typography gutterBottom>
          {RegisterFormattedMessages.verificationModalBody}
          {` ${phoneNumber}`}.
        </Typography>

        <TextField
          type="text"
          name="verificationCode"
          placeholder="XXXXXX"
          label="Verification Code"
          size="small"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          fullWidth
          autoFocus
          required
          sx={{ marginBottom: 1 }}
          slotProps={{
            htmlInput: { maxLength: 6 },
          }}
        />

        <Typography gutterBottom>
          {RegisterFormattedMessages.resendCodeText} {resendLink}
        </Typography>

        {setError()}

        <Box display="flex" justifyContent="end">
          {verifyingCode ? (
            <Typography>{RegisterFormattedMessages.sending}</Typography>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleVerification}
            >
              {RegisterFormattedMessages.submit}
            </Button>
          )}
        </Box>
      </CustomModal>
    );
  };

  /*
    Render the Register Form components based on state
  */
  const renderForm = () => {
    let signupText = RegisterFormattedMessages.signUpText;
    let signupLoadingText = RegisterFormattedMessages.signUpLoadingText;
    let successText = RegisterFormattedMessages.successText;

    // User has completed the Registration process
    if (hasRegistered) {
      return (
        <Stack gap={2} justifyContent="center" alignItems="center">
          <Typography variant="h4">{successText}</Typography>
          <Typography textAlign="center">
            {RegisterFormattedMessages.validateText}
          </Typography>

          <Box>
            <MuiLink component={Link} to="/">
              {RegisterFormattedMessages.backToLogin}
            </MuiLink>
          </Box>
        </Stack>
      );
    }
    // User has not completed the Registration process
    else {
      return (
        <Box
          component="form"
          onSubmit={handleSubmit}
          onChange={handleInputChange}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputLabel required>
                {RegisterFormattedMessages.firstNameText}
              </InputLabel>
              <TextField
                type="text"
                size="small"
                value={firstName}
                name="firstName"
                required
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <InputLabel required>
                {RegisterFormattedMessages.lastNameText}
              </InputLabel>
              <TextField
                type="text"
                size="small"
                value={lastName}
                name="lastName"
                required
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <InputLabel required>
                  {RegisterFormattedMessages.emailText}
                </InputLabel>

                <HelpTooltip
                  message={RegisterFormattedMessages.workEmailText}
                  sx={{ marginBottom: 1 }}
                />
              </Box>

              <TextField
                type="text"
                size="small"
                value={email}
                name="email"
                required
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <InputLabel required>
                  {RegisterFormattedMessages.phoneNumberText}
                </InputLabel>

                <HelpTooltip
                  message={RegisterFormattedMessages.whitelistText}
                  sx={{ marginBottom: 1 }}
                />
              </Box>

              <TextField
                type="text"
                size="small"
                value={phoneNumber}
                name="phoneNumber"
                required
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <InputLabel required>Password</InputLabel>

                <HelpTooltip
                  message={RegisterFormattedMessages.passwordFormat}
                  sx={{ marginBottom: 1 }}
                />
              </Box>

              <TextField
                type={showPassword ? 'text' : 'password'}
                value={password}
                name="password"
                required
                size="small"
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
                error={!validPassword}
                helperText={
                  !validPassword ? 'Password is not strong enough.' : ''
                }
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <InputLabel required>Confirm Password</InputLabel>
              <TextField
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                name="confirmPassword"
                required
                size="small"
                autoComplete="new-password"
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!validConfirmPassword}
                helperText={
                  !validConfirmPassword ? "Passwords don't match." : ''
                }
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <InputLabel required>
                {RegisterFormattedMessages.companyText}
              </InputLabel>
              <TextField
                type="text"
                size="small"
                value={company}
                name="company"
                required
                onChange={(e) => setCompany(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <InputLabel required>
                {RegisterFormattedMessages.countryText}
              </InputLabel>
              {countryDropDown()}
            </Grid>

            <Grid size={12} textAlign="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={signUpAgreement}
                    onChange={(e) => setSignUpAgreement(e.target.checked)}
                    name="signUpAgreement"
                  />
                }
                label={
                  <>
                    {`By signing up, I agree to `}
                    <MuiLink
                      href="https://redoxygen.com/service-agreement/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </MuiLink>
                    {` and `}
                    <MuiLink
                      href="https://redoxygen.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </MuiLink>
                  </>
                }
              />
            </Grid>

            <Grid size={12}>
              <Divider />
            </Grid>

            <Grid size={12}>{setError()}</Grid>

            <Grid size={12}>
              <LoaderButton
                block
                bssize="large"
                disabled={!signUpAgreement}
                type="submit"
                isLoading={isLoading}
                text={signupText}
                loadingText={signupLoadingText}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      );
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <FullscreenLayout fullHeight>
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          gap: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'common.white',
            borderRadius: 4,
            paddingTop: 2,
            paddingBottom: 4,
            paddingX: { xs: 2, md: 4 },
            width: 800,
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 2,
            }}
          >
            <BackButton
              onClick={backToLogin}
              backButtonText={RegisterFormattedMessages.backToLogin}
            />

            <Button
              onClick={contactSupport}
              endIcon={<ArrowForward />}
              color="secondary"
            >
              {RegisterFormattedMessages.contactSupport}
            </Button>
          </Box>

          {renderForm()}
        </Box>
      </Stack>

      {showVerificationDialog()}
    </FullscreenLayout>
  );
};

export default Register;
